import { isRight } from 'fp-ts/Either';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Languages,
  Loader2,
  RefreshCw,
  Save,
  Trophy,
  Undo2,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import { Button } from '../../components/ui/button';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import { extractErrorInfo } from '../../utils/errorInfo';
import { DappPickerDialog } from './DappPickerDialog';
import { QuestWorkspaceEditor } from './QuestWorkspaceEditor';
import {
  EditorSkeleton,
  minimalButtonClassName,
  Skeleton,
  WorkspaceNavSkeleton,
} from './QuestWorkspaceEditorLayout';
import { QuestWorkspaceNav } from './QuestWorkspaceNav';
import {
  addAppToWorkspace,
  addCredentialToWorkspace,
  addQuestToWorkspace,
  addRoundToWorkspace,
  addSeasonToWorkspace,
  createLocalId,
  descendantIssueCounts,
  isPersistedId,
  moveAppWithinRound,
  parentRefOf,
  patchSelectionPreview,
  patchWorkspace as patchWorkspaceItem,
  removeFromWorkspace,
  resolveSelection,
  selfAndAncestorKeys,
  type AppMoveEdge,
  type QuestWorkspaceCollectionKey,
} from './questWorkspaceLocalState';
import {
  createInitialQuestWorkspace,
  parseQuestWorkspaceTargetKey,
  questWorkspaceTargetKey,
  roundDisplayName,
  selectionPreviewToSkrBaseUnits,
  selectionPreviewToSkrUnits,
  validateQuestWorkspaceDetailed,
  workspaceToSkrBaseUnits,
  workspaceToSkrUnits,
  type QuestSelectionPreviewForm,
  type QuestWorkspaceForm,
  type QuestWorkspaceItemRef,
} from './questWorkspace';

type GeneratedCredential = {
  id: string;
  label: string;
  keyPrefix: string;
  apiKey: string;
};

type TranslationSummary = {
  pendingCount: number;
  completeCount: number;
  errorCount: number;
  totalCount: number;
  completedLanguageCount: number;
  languageCount: number;
};

function snapshot(workspace: QuestWorkspaceForm): string {
  return JSON.stringify(workspace);
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function summarizeMetadataTranslations(
  metadataJson: string,
  summary: TranslationSummary,
) {
  let metadata: Record<string, unknown>;
  try {
    metadata = readRecord(JSON.parse(metadataJson));
  } catch {
    return;
  }

  const statuses = readRecord(metadata.i18nStatus);
  for (const value of Object.values(statuses)) {
    const status = readRecord(value);
    const state = status.state;
    if (state !== 'pending' && state !== 'complete' && state !== 'error') {
      continue;
    }

    summary.totalCount += 1;
    if (
      typeof status.completedLanguageCount === 'number' &&
      typeof status.languageCount === 'number'
    ) {
      summary.completedLanguageCount += status.completedLanguageCount;
      summary.languageCount += status.languageCount;
    }
    if (state === 'pending') {
      summary.pendingCount += 1;
    } else if (state === 'complete') {
      summary.completeCount += 1;
    } else {
      summary.errorCount += 1;
    }
  }
}

function summarizeWorkspaceTranslations(
  workspace: QuestWorkspaceForm,
): TranslationSummary {
  const summary = {
    pendingCount: 0,
    completeCount: 0,
    errorCount: 0,
    totalCount: 0,
    completedLanguageCount: 0,
    languageCount: 0,
  };

  for (const season of workspace.seasons) {
    summarizeMetadataTranslations(season.metadataJson, summary);
  }
  for (const round of workspace.rounds) {
    summarizeMetadataTranslations(round.metadataJson, summary);
  }
  for (const app of workspace.apps) {
    summarizeMetadataTranslations(app.metadataJson, summary);
  }
  for (const quest of workspace.quests) {
    summarizeMetadataTranslations(quest.metadataJson, summary);
  }

  return summary;
}

function SaveStateBadge(props: { isDirty: boolean; savedAt: Date | null }) {
  // Borderless: this reads as the leading segment inside the bordered save
  // cluster, not as its own standalone pill.
  if (props.isDirty) {
    return (
      <span className="hidden items-center gap-1.5 px-1.5 text-xs font-medium text-amber-300 sm:flex">
        <span className="size-1.5 rounded-full bg-amber-400" />
        Unsaved changes
      </span>
    );
  }
  if (props.savedAt) {
    return (
      <span className="hidden items-center gap-1.5 px-1.5 text-xs text-neutral-400 sm:flex">
        <CheckCircle2 className="size-3.5 text-emerald-400" />
        Saved{' '}
        {props.savedAt.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </span>
    );
  }
  return null;
}

function TranslationStateBadge(props: { summary: TranslationSummary }) {
  const { summary } = props;

  if (summary.pendingCount > 0) {
    const progress =
      summary.languageCount > 0
        ? `${summary.completedLanguageCount}/${summary.languageCount}`
        : String(summary.pendingCount);

    return (
      <span className="hidden items-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-xs font-medium text-sky-300 sm:flex">
        <Loader2 className="size-3.5 animate-spin" />
        Translating {progress}
      </span>
    );
  }

  if (summary.errorCount > 0) {
    return (
      <span className="hidden items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs font-medium text-rose-300 sm:flex">
        <AlertCircle className="size-3.5" />
        Translation errors {summary.errorCount}
      </span>
    );
  }

  if (summary.completeCount > 0) {
    return (
      <span className="hidden items-center gap-1.5 rounded-full border border-neutral-800 bg-neutral-900/60 px-2.5 py-1 text-xs text-neutral-400 sm:flex">
        <Languages className="size-3.5 text-teal-300" />
        Translated
      </span>
    );
  }

  return null;
}

function IssuesButton(props: {
  count: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-expanded={props.isOpen}
      className={cn(
        minimalButtonClassName,
        'min-w-[7rem] justify-center',
        props.count > 0 &&
          'border-rose-500/40 text-rose-300 hover:border-rose-400/60 hover:bg-rose-500/10 hover:text-rose-200',
      )}
      onClick={props.onToggle}
    >
      {props.count > 0 ? (
        <AlertCircle className="size-4" />
      ) : (
        <CheckCircle2 className="size-4 text-emerald-400" />
      )}
      {props.count > 0
        ? `${props.count} issue${props.count === 1 ? '' : 's'}`
        : 'No issues'}
    </Button>
  );
}

function IssuesPanel(props: {
  issues: ReturnType<typeof validateQuestWorkspaceDetailed>;
  onJump: (issue: QuestWorkspaceItemRef, field?: string) => void;
  onClose: () => void;
}) {
  // Dedupe by target + field + message so two different items with the same
  // validation text each stay reachable, while a true duplicate collapses.
  const issueKey = (issue: (typeof props.issues)[number]) =>
    `${questWorkspaceTargetKey(issue.target)}::${issue.field ?? ''}::${issue.message}`;
  const seen = new Set<string>();
  const unique = props.issues.filter((issue) => {
    const key = issueKey(issue);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return (
    <div className="absolute right-4 top-full z-30 mt-2 w-[min(26rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl shadow-black/50">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-800 px-4 py-2.5">
        <h2 className="text-sm font-semibold text-white">
          {unique.length === 0
            ? 'No validation issues'
            : `${unique.length} validation issue${
                unique.length === 1 ? '' : 's'
              }`}
        </h2>
        <button
          type="button"
          aria-label="Close issues panel"
          onClick={props.onClose}
          className="rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
        >
          <X className="size-4" />
        </button>
      </div>
      {unique.length === 0 ? (
        <div className="flex items-center gap-2 px-4 py-4 text-sm text-emerald-300">
          <CheckCircle2 className="size-4" />
          Everything looks valid.
        </div>
      ) : (
        <ul className="max-h-[60vh] overflow-y-auto p-1.5">
          {unique.map((issue) => (
            <li key={issueKey(issue)}>
              <button
                type="button"
                onClick={() => props.onJump(issue.target, issue.field)}
                className="flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-neutral-300 transition-colors hover:bg-rose-500/10 hover:text-rose-100"
              >
                <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-rose-400" />
                <span>{issue.message}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GeneratedCredentialsOverlay(props: {
  credentials: GeneratedCredential[];
  onDismiss: () => void;
}) {
  if (props.credentials.length === 0) {
    return null;
  }

  const copyApiKey = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success('API key copied');
    } catch {
      toast.error('Could not copy the API key');
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-40 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-amber-500/30 bg-neutral-900 p-4 shadow-2xl shadow-black/50"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-amber-100">
            New credential API keys
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-amber-100/70">
            Copy these keys now. Only their hashes are stored after this save.
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss generated API keys"
          onClick={props.onDismiss}
          className="shrink-0 rounded-md p-1 text-amber-100/70 transition-colors hover:bg-amber-400/10 hover:text-white"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {props.credentials.map((credential) => (
          <div
            key={credential.id}
            className="flex flex-col gap-2 rounded-lg border border-amber-500/20 bg-neutral-950/40 p-3"
          >
            <div className="min-w-0">
              <div className="truncate text-xs font-medium text-amber-50">
                {credential.label || credential.keyPrefix}
              </div>
              <code className="mt-1 block break-all font-mono text-xs text-amber-100/80">
                {credential.apiKey}
              </code>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-end"
              onClick={() => copyApiKey(credential.apiKey)}
            >
              <Copy className="size-4" />
              Copy
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function QuestsPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'superadmin',
  });
  const trpc = useTRPC();

  usePageTitle('Quests | Admin');

  const [workspace, setWorkspace] = useState<QuestWorkspaceForm>(() =>
    createInitialQuestWorkspace(),
  );
  const [selected, setSelected] = useState<QuestWorkspaceItemRef | null>(null);
  const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [hasValidated, setHasValidated] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [appPickerRoundId, setAppPickerRoundId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRetryingTranslations, setIsRetryingTranslations] = useState(false);
  const [isRunningSelectionPreview, setIsRunningSelectionPreview] =
    useState(false);
  const [isRevealingAllocations, setIsRevealingAllocations] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState<
    GeneratedCredential[]
  >([]);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const savedWorkspaceRef = useRef<QuestWorkspaceForm | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasNavigatedRef = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const selParam = searchParams.get('sel');
  // Set right before we write `?sel=` ourselves so the hydrate effect that the
  // write triggers doesn't echo straight back into another write.
  const skipNextSelWriteRef = useRef(false);

  useEffect(() => {
    if (authState.type !== 'Authenticated') {
      return;
    }

    let cancelled = false;
    setIsLoadingWorkspace(true);
    setLoadError(null);

    void trpc.getQuestWorkspace
      .query()
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (isRight(result)) {
          const editorWorkspace = workspaceToSkrUnits(result.right);
          setWorkspace(editorWorkspace);
          savedWorkspaceRef.current = editorWorkspace;
          setSavedSnapshot(snapshot(editorWorkspace));
          // Selection is governed by the `?sel=` URL param (see the hydrate
          // effect below), so a deep-linked node survives the workspace load
          // instead of being reset to the Overview here.
          return;
        }

        setLoadError(result.left.message || 'Could not load quest workspace.');
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Could not load quest workspace.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingWorkspace(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authState.type, trpc]);

  // URL → selection. The `?sel=` param is the serialized target key (e.g.
  // `quest:<id>`); a missing or unrecognized value falls back to the Overview.
  // Runs on mount and on browser back/forward. The parsed ref is reused when it
  // names the same node so we don't churn a fresh object (which would split the
  // sync handshake into an extra render); resolveSelection tolerates an id that
  // isn't in the workspace yet, so hydrating before the load finishes is safe.
  useLayoutEffect(() => {
    const parsed = selParam ? parseQuestWorkspaceTargetKey(selParam) : null;
    skipNextSelWriteRef.current = true;
    setSelected((prev) => {
      const prevKey = prev ? questWorkspaceTargetKey(prev) : null;
      const nextKey = parsed ? questWorkspaceTargetKey(parsed) : null;
      return prevKey === nextKey ? prev : parsed;
    });
  }, [selParam]);

  const selection = resolveSelection(workspace, selected);
  const selectionKey = questWorkspaceTargetKey(selection);

  const issues = useMemo(
    () => validateQuestWorkspaceDetailed(workspace),
    [workspace],
  );

  const issueCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const issue of issues) {
      const key = questWorkspaceTargetKey(issue.target);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [issues]);

  const rolledUpIssueCounts = useMemo(
    () => descendantIssueCounts(workspace, issues),
    [workspace, issues],
  );

  const itemErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!hasValidated) {
      return errors;
    }
    for (const issue of issues) {
      if (issue.crossItem) {
        continue;
      }
      if (questWorkspaceTargetKey(issue.target) !== selectionKey) {
        continue;
      }
      if (issue.field && !errors[issue.field]) {
        errors[issue.field] = issue.message;
      }
    }
    return errors;
  }, [hasValidated, issues, selectionKey]);

  const generatedCredentialApiKeys = useMemo(
    () =>
      new Map<string, string>(
        generatedCredentials.map((credential) => [
          credential.id,
          credential.apiKey,
        ]),
      ),
    [generatedCredentials],
  );

  const currentSnapshot = useMemo(() => snapshot(workspace), [workspace]);
  const isDirty = savedSnapshot !== null && currentSnapshot !== savedSnapshot;

  // Show the loading skeleton until the first real workspace arrives, so the
  // placeholder default tree never flashes before the data loads.
  const showSkeleton =
    isLoadingWorkspace && !loadError && savedSnapshot === null;
  const translationSummary = useMemo(
    () => summarizeWorkspaceTranslations(workspace),
    [workspace],
  );

  useEffect(() => {
    if (
      authState.type !== 'Authenticated' ||
      showSkeleton ||
      isDirty ||
      translationSummary.pendingCount === 0
    ) {
      return;
    }

    let cancelled = false;
    const refreshWorkspace = async () => {
      try {
        const result = await trpc.getQuestWorkspace.query();
        if (cancelled || !isRight(result)) {
          return;
        }

        const editorWorkspace = workspaceToSkrUnits(result.right);
        setWorkspace(editorWorkspace);
        savedWorkspaceRef.current = editorWorkspace;
        setSavedSnapshot(snapshot(editorWorkspace));
      } catch {
        // Keep the last saved workspace visible. The next poll will retry.
      }
    };

    const firstRefresh = window.setTimeout(refreshWorkspace, 1500);
    const interval = window.setInterval(refreshWorkspace, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(firstRefresh);
      window.clearInterval(interval);
    };
  }, [
    authState.type,
    isDirty,
    showSkeleton,
    translationSummary.pendingCount,
    trpc,
  ]);

  // selection → URL. Mirror the active node into `?sel=` (replace, so paging
  // through the tree doesn't stack history). The Overview is represented by the
  // absence of the param to keep URLs clean. The skip flag is drained BEFORE the
  // skeleton gate: otherwise a skip armed by the mount hydrate would survive the
  // whole load window and then swallow the first post-load reconciliation write
  // (e.g. clearing a `?sel=` that points at a since-deleted node, which resolves
  // to the Overview). Writes themselves stay gated until the workspace has
  // loaded so the transient default tree can't clobber a deep-linked selection.
  useEffect(() => {
    if (skipNextSelWriteRef.current) {
      skipNextSelWriteRef.current = false;
      return;
    }
    if (showSkeleton) {
      return;
    }
    const desired = selectionKey === 'overview' ? null : selectionKey;
    if (desired === searchParams.get('sel')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (desired) {
      next.set('sel', desired);
    } else {
      next.delete('sel');
    }
    setSearchParams(next, { replace: true });
  }, [selectionKey, showSkeleton, searchParams, setSearchParams]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Bring a freshly selected item into view and move focus to its heading so
  // navigation never leaves the reader stranded mid-scroll. Skip the very first
  // run so simply landing on the page doesn't steal focus to the heading.
  useEffect(() => {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }
    const root = scrollRef.current;
    if (!root) {
      return;
    }
    root.scrollTo({ top: 0 });
    const frame = requestAnimationFrame(() => {
      root
        .querySelector<HTMLElement>('[data-editor-heading]')
        ?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectionKey]);

  if (!canRender || authState.type !== 'Authenticated') {
    return null;
  }

  const expandKeys = (keys: string[]) => {
    setCollapsedKeys((current) => {
      if (!keys.some((key) => current.has(key))) {
        return current;
      }

      const next = new Set(current);
      for (const key of keys) {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleCollapsed = (ref: QuestWorkspaceItemRef) => {
    const key = questWorkspaceTargetKey(ref);
    setCollapsedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const expandAll = () => setCollapsedKeys(new Set());

  const collapseAll = () => {
    const next = new Set<string>();
    for (const season of workspace.seasons) {
      next.add(questWorkspaceTargetKey({ kind: 'season', id: season.id }));
    }
    for (const round of workspace.rounds) {
      next.add(questWorkspaceTargetKey({ kind: 'round', id: round.id }));
    }
    for (const app of workspace.apps) {
      next.add(questWorkspaceTargetKey({ kind: 'app', id: app.id }));
    }
    setCollapsedKeys(next);
  };

  const selectItem = (ref: QuestWorkspaceItemRef | null) => {
    if (ref && ref.kind !== 'selection' && ref.kind !== 'overview') {
      const parent = parentRefOf(workspace, ref);
      if (parent) {
        expandKeys(selfAndAncestorKeys(workspace, parent));
      }
    }
    setSelected(ref);
  };

  const jumpToIssue = (ref: QuestWorkspaceItemRef, field?: string) => {
    setHasValidated(true);
    selectItem(ref);
    setIssuesOpen(false);
    if (!field) {
      return;
    }
    const fieldId =
      ref.kind === 'selection'
        ? `selection-${field}`
        : ref.kind === 'overview'
          ? null
          : `${ref.kind}-${ref.id}-${field}`;
    if (!fieldId) {
      return;
    }
    requestAnimationFrame(() => {
      const input = document.getElementById(fieldId);
      if (input) {
        // Advanced fields live inside a collapsed <details>; open it first so
        // the field is actually visible and focusable.
        input.closest('details')?.setAttribute('open', '');
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        (input as HTMLElement).focus({ preventScroll: true });
      }
    });
  };

  const patchWorkspace = (
    key: QuestWorkspaceCollectionKey,
    id: string,
    patch: Record<string, unknown>,
  ) => {
    setWorkspace((current) => patchWorkspaceItem(current, key, id, patch));
  };

  const updateSelectionPreview = (
    patch: Partial<QuestSelectionPreviewForm>,
  ) => {
    setWorkspace((current) => patchSelectionPreview(current, patch));
  };

  const addSeason = () => {
    const id = createLocalId('season');
    setWorkspace((current) => addSeasonToWorkspace(current, id));
    setSelected({ kind: 'season', id });
  };

  const addRound = (seasonId: string) => {
    const id = createLocalId('round');
    expandKeys(
      selfAndAncestorKeys(workspace, { kind: 'season', id: seasonId }),
    );
    setWorkspace((current) => addRoundToWorkspace(current, seasonId, id));
    setSelected({ kind: 'round', id });
  };

  const addApp = (roundId: string, prefill?: Record<string, string>) => {
    const id = createLocalId('app');
    expandKeys(selfAndAncestorKeys(workspace, { kind: 'round', id: roundId }));
    setWorkspace((current) => addAppToWorkspace(current, roundId, id, prefill));
    setSelected({ kind: 'app', id });
  };

  const requestAddApp = (roundId: string) => setAppPickerRoundId(roundId);

  const confirmAddApp = (prefill?: Record<string, string>) => {
    if (appPickerRoundId) {
      addApp(appPickerRoundId, prefill);
    }
    setAppPickerRoundId(null);
  };

  const addQuest = (appId: string) => {
    const id = createLocalId('quest');
    expandKeys(selfAndAncestorKeys(workspace, { kind: 'app', id: appId }));
    setWorkspace((current) => addQuestToWorkspace(current, appId, id));
    setSelected({ kind: 'quest', id });
  };

  const moveApp = (appId: string, targetAppId: string, edge: AppMoveEdge) => {
    setWorkspace((current) =>
      moveAppWithinRound(current, appId, targetAppId, edge),
    );
  };

  const addCredential = (appId: string) => {
    const id = createLocalId('credential');
    expandKeys(selfAndAncestorKeys(workspace, { kind: 'app', id: appId }));
    setWorkspace((current) => addCredentialToWorkspace(current, appId, id));
    setSelected({ kind: 'credential', id });
  };

  const removeItem = (ref: QuestWorkspaceItemRef) => {
    const parent = parentRefOf(workspace, ref);
    setWorkspace((current) => removeFromWorkspace(current, ref));
    selectItem(parent ?? { kind: 'overview' });
  };

  const previewRound = (roundId: string) => {
    updateSelectionPreview({ selectedRoundId: roundId });
    setSelected({ kind: 'selection' });
  };

  const discardChanges = () => {
    if (savedWorkspaceRef.current) {
      setWorkspace(savedWorkspaceRef.current);
    }
    setHasValidated(false);
    setDiscardOpen(false);
    setSelected({ kind: 'overview' });
  };

  const handleSave = async () => {
    setHasValidated(true);
    const result = validateQuestWorkspaceDetailed(workspace);

    if (result.length > 0) {
      setIssuesOpen(true);
      toast.error(
        `${result.length} validation issue${
          result.length === 1 ? '' : 's'
        } found. Nothing was saved.`,
      );
      return;
    }

    setIsSaving(true);
    try {
      const saveResult = await trpc.saveQuestWorkspace.mutate(
        workspaceToSkrBaseUnits(workspace),
      );
      if (isRight(saveResult)) {
        const editorWorkspace = workspaceToSkrUnits(saveResult.right.workspace);
        setWorkspace(editorWorkspace);
        savedWorkspaceRef.current = editorWorkspace;
        setSavedSnapshot(snapshot(editorWorkspace));
        setSavedAt(new Date());
        setGeneratedCredentials(saveResult.right.generatedCredentials);
        // Keep the admin on the node they were editing rather than bouncing to
        // the Overview. resolveSelection keeps a persisted selection (its UUID
        // is unchanged by the save) and only falls back to the Overview if the
        // selection was a brand-new draft whose id the server just replaced.
        setHasValidated(false);
        setIssuesOpen(false);
        toast.success('Quest workspace saved');
        return;
      }

      toast.error(saveResult.left.message || 'Could not save workspace.');
    } catch (error) {
      toast.error(
        extractErrorInfo(error, {
          defaultMessage: 'Could not save workspace.',
        }).message,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const retryFailedTranslations = async () => {
    if (isDirty) {
      toast.error('Save or discard changes before retrying translations.');
      return;
    }
    if (translationSummary.errorCount === 0) {
      toast.error('There are no failed translations to retry.');
      return;
    }

    setIsRetryingTranslations(true);
    try {
      const result = await trpc.retryQuestWorkspaceTranslations.mutate();
      if (isRight(result)) {
        const editorWorkspace = workspaceToSkrUnits(result.right.workspace);
        setWorkspace(editorWorkspace);
        savedWorkspaceRef.current = editorWorkspace;
        setSavedSnapshot(snapshot(editorWorkspace));
        if (result.right.retryCount === 0) {
          toast.success('No failed translations need retrying');
        } else {
          toast.success(
            result.right.retryCount === 1
              ? 'Retrying 1 failed translation'
              : `Retrying ${result.right.retryCount} failed translations`,
          );
        }
        return;
      }

      toast.error(
        result.left.message || 'Could not retry failed translations.',
      );
    } catch (error) {
      toast.error(
        extractErrorInfo(error, {
          defaultMessage: 'Could not retry failed translations.',
        }).message,
      );
    } finally {
      setIsRetryingTranslations(false);
    }
  };

  // Preview/allocation results are server-confirmed state, not unsaved user
  // edits. Fold them into the saved baseline too so they don't flip the
  // workspace into a spurious "Unsaved changes" state (and so Discard can't
  // throw the freshly fetched data away).
  const syncServerSelectionPreview = (
    nextPreview: QuestSelectionPreviewForm,
  ) => {
    // The backend speaks reward amounts in base units; the editor works in SKR.
    const skrPreview = selectionPreviewToSkrUnits(nextPreview);
    setWorkspace((current) => ({ ...current, selectionPreview: skrPreview }));
    const baseline = savedWorkspaceRef.current;
    if (baseline) {
      const nextBaseline = { ...baseline, selectionPreview: skrPreview };
      savedWorkspaceRef.current = nextBaseline;
      setSavedSnapshot(snapshot(nextBaseline));
    }
  };

  const runSelectionPreview = async () => {
    const roundId = workspace.selectionPreview.selectedRoundId;
    if (!roundId) {
      toast.error('Select a round before running a preview.');
      return;
    }
    if (!isPersistedId(roundId)) {
      toast.error('Save the workspace before running a new round preview.');
      return;
    }

    setIsRunningSelectionPreview(true);
    try {
      const result = await trpc.runQuestSelectionPreview.mutate({ roundId });
      if (isRight(result)) {
        syncServerSelectionPreview(result.right.selectionPreview);
        toast.success('Selection preview completed');
        return;
      }

      toast.error(result.left.message || 'Could not run selection preview.');
    } catch {
      toast.error('Could not run selection preview.');
    } finally {
      setIsRunningSelectionPreview(false);
    }
  };

  const revealAllocations = async () => {
    const roundId = workspace.selectionPreview.selectedRoundId;
    if (!roundId) {
      toast.error('Select a round before revealing allocations.');
      return;
    }
    if (!isPersistedId(roundId)) {
      toast.error('Save the workspace before revealing new round allocations.');
      return;
    }

    setIsRevealingAllocations(true);
    try {
      const result = await trpc.revealQuestAllocations.mutate(
        selectionPreviewToSkrBaseUnits(workspace.selectionPreview),
      );
      if (isRight(result)) {
        syncServerSelectionPreview(result.right.selectionPreview);
        toast.success(`${result.right.allocationCount} allocations revealed`);
        return;
      }

      toast.error(result.left.message || 'Could not reveal allocations.');
    } catch {
      toast.error('Could not reveal allocations.');
    } finally {
      setIsRevealingAllocations(false);
    }
  };

  const loadQuestCompletions = async (questId: string) => {
    const result = await trpc.listQuestCompletions.query({ questId });
    if (isRight(result)) {
      return result.right;
    }

    throw new Error(result.left.message || 'Could not load quest requests.');
  };

  const pickerRoundIndex = workspace.rounds.findIndex(
    (round) => round.id === appPickerRoundId,
  );
  const pickerRound = workspace.rounds[pickerRoundIndex];

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <header className="sticky top-0 z-20 border-b border-neutral-800 bg-neutral-900/85 backdrop-blur-md">
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3.5 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-300 ring-1 ring-inset ring-teal-500/20">
              <Trophy className="size-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-white">Quests</h1>
              <p className="truncate text-sm text-neutral-400">
                {showSkeleton ? (
                  'Loading workspace…'
                ) : (
                  <>
                    {workspace.seasons.length} season
                    {workspace.seasons.length === 1 ? '' : 's'} ·{' '}
                    {workspace.rounds.length} round
                    {workspace.rounds.length === 1 ? '' : 's'} ·{' '}
                    {workspace.quests.length} quest
                    {workspace.quests.length === 1 ? '' : 's'}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 md:gap-3">
            {showSkeleton ? (
              <>
                <Skeleton className="h-8 w-28" />
                <Button
                  type="button"
                  size="sm"
                  className="bg-teal-600 text-white hover:bg-teal-500"
                  disabled
                >
                  <Save className="size-4" />
                  Save workspace
                </Button>
              </>
            ) : (
              <>
                {/* Status zone — quiet workspace-health signals. */}
                <div className="flex flex-wrap items-center gap-2">
                  <TranslationStateBadge summary={translationSummary} />
                  {translationSummary.errorCount > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className={minimalButtonClassName}
                      disabled={isRetryingTranslations || isSaving}
                      onClick={retryFailedTranslations}
                    >
                      {isRetryingTranslations ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <RefreshCw className="size-4" />
                      )}
                      {isRetryingTranslations
                        ? 'Retrying'
                        : 'Retry translations'}
                    </Button>
                  )}
                  <IssuesButton
                    count={issues.length}
                    isOpen={issuesOpen}
                    onToggle={() => {
                      setHasValidated(true);
                      setIssuesOpen((current) => !current);
                    }}
                  />
                </div>

                {/* Save zone — the dirty state, Discard, and Save read as one
                    bound unit so Save is the single obvious primary. */}
                <div className="flex items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900/50 p-1 sm:pl-2.5">
                  <SaveStateBadge isDirty={isDirty} savedAt={savedAt} />
                  {isDirty && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className={minimalButtonClassName}
                      onClick={() => setDiscardOpen(true)}
                    >
                      <Undo2 className="size-4" />
                      Discard
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    className="bg-teal-600 text-white hover:bg-teal-500"
                    disabled={isSaving}
                    onClick={handleSave}
                  >
                    {isSaving ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    {isSaving ? 'Saving' : 'Save workspace'}
                  </Button>
                </div>
              </>
            )}
          </div>

          {issuesOpen && (
            <IssuesPanel
              issues={issues}
              onJump={jumpToIssue}
              onClose={() => setIssuesOpen(false)}
            />
          )}
        </div>
      </header>

      {pickerRound && (
        <DappPickerDialog
          title={`Add app to ${roundDisplayName(pickerRound, pickerRoundIndex)}`}
          description="Pick a dapp from the portal catalog to prefill the new app, or start blank."
          blankLabel="Start with a blank app"
          onBlank={() => confirmAddApp()}
          onClose={() => setAppPickerRoundId(null)}
          onPick={confirmAddApp}
        />
      )}

      <GeneratedCredentialsOverlay
        credentials={generatedCredentials}
        onDismiss={() => setGeneratedCredentials([])}
      />

      <div className="mx-auto max-w-7xl px-4 py-5 md:px-8">
        {loadError && (
          <div
            role="alert"
            className="mb-5 rounded-xl border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-200"
          >
            {loadError}
          </div>
        )}

        {showSkeleton ? (
          <div className="lg:grid lg:grid-cols-[288px_minmax(0,1fr)] lg:items-start lg:gap-6">
            <div className="mb-6 lg:mb-0">
              <WorkspaceNavSkeleton />
            </div>
            <div className="min-w-0 max-w-4xl">
              <EditorSkeleton />
            </div>
          </div>
        ) : (
          <div className="lg:grid lg:grid-cols-[288px_minmax(0,1fr)] lg:items-start lg:gap-6">
            <div className="mb-6 lg:sticky lg:top-[4.75rem] lg:mb-0 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <QuestWorkspaceNav
                workspace={workspace}
                selected={selection}
                issueCounts={issueCounts}
                descendantIssueCounts={rolledUpIssueCounts}
                collapsedKeys={collapsedKeys}
                onSelect={selectItem}
                onToggleCollapse={toggleCollapsed}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                onAddSeason={addSeason}
                onAddRound={addRound}
                onAddApp={requestAddApp}
                onAddQuest={addQuest}
                onMoveApp={moveApp}
              />
            </div>

            <div className="min-w-0 max-w-4xl">
              <QuestWorkspaceEditor
                workspace={workspace}
                selected={selection}
                itemErrors={itemErrors}
                issues={issues}
                generatedCredentialApiKeys={generatedCredentialApiKeys}
                onSelect={selectItem}
                onPatch={patchWorkspace}
                onPatchSelection={updateSelectionPreview}
                onRemove={removeItem}
                onAddRound={addRound}
                onAddApp={requestAddApp}
                onAddQuest={addQuest}
                onAddCredential={addCredential}
                onPreviewRound={previewRound}
                onLoadQuestCompletions={loadQuestCompletions}
                onRunSelectionPreview={runSelectionPreview}
                onRevealAllocations={revealAllocations}
                isRunningSelectionPreview={isRunningSelectionPreview}
                isRevealingAllocations={isRevealingAllocations}
              />
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={discardOpen}
        title="Discard changes"
        message="Revert the workspace to the last saved version? Your unsaved edits will be lost."
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={discardChanges}
        onCancel={() => setDiscardOpen(false)}
      />

      <ConfirmationDialog
        isOpen={blocker.state === 'blocked'}
        title="Leave with unsaved changes?"
        message="You have unsaved changes to the quest workspace. Leaving now will discard them."
        confirmText="Leave page"
        cancelText="Stay"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
